import { AfterViewInit, Directive, ElementRef, Input, OnDestroy, Renderer2 } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatSelect } from '@angular/material/select';
import { MatOption } from '@angular/material/core';
import { MatFormField } from '@angular/material/form-field';
import { Subject } from 'rxjs';
import { takeUntil, debounceTime } from 'rxjs/operators';

@Directive({
  selector: 'mat-select[searchable]'
})
export class SearchableDropdownDirective implements AfterViewInit, OnDestroy {
  @Input() displayKey?: string;
  @Input() isObjectsList = false;

  private searchControl = new FormControl('');
  private originalOptions: MatOption[] = [];
  private destroy$ = new Subject<void>();
  private searchGroup: HTMLElement | null = null;

  constructor(
    private matSelect: MatSelect,
    private renderer: Renderer2
  ) {}

  ngAfterViewInit() {
    setTimeout(() => {
      if (this.matSelect.options) {
        this.originalOptions = this.matSelect.options.toArray();
      }
    });

    this.matSelect.openedChange
      .pipe(takeUntil(this.destroy$))
      .subscribe(opened => {
        if (opened) {
          this.addSearchInput();
          this.resetSearchInputField();
        } else {
          this.searchControl.setValue('', { emitEvent: false });
          this.filterOptions('');
        }
      });

    this.searchControl.valueChanges
      .pipe(
        takeUntil(this.destroy$),
        debounceTime(100)
      )
      .subscribe(value => {
        const searchTerm = value?.toLowerCase() || '';
        this.filterOptions(searchTerm);
      });
  }

  private resetSearchInputField() {
    if (this.searchGroup) {
      const input = this.searchGroup.querySelector('input');
      if (input) {
        (input as HTMLInputElement).value = ''; // Clear the input field
      }
    }
  }
  

  private addSearchInput() {
      const panel = this.matSelect.panel.nativeElement;
      
      if (!this.searchGroup) {
        // Create mat-optgroup with search input
        this.searchGroup = this.renderer.createElement('mat-optgroup');
        this.renderer.setAttribute(this.searchGroup, 'class', 'mat-mdc-optgroup m-0 p-0 w-100');
        
        // Create the form field with input
        const formFieldHtml = `
          <span class="mat-mdc-optgroup-label">
            <span class="mdc-list-item__primary-text">
              <mat-form-field appearance="outline" class="mat-mdc-form-field m-0 p-2  w-100 mat-mdc-form-field-type-mat-input mat-form-field-appearance-outline mat-primary">
                <div class="mat-mdc-text-field-wrapper mdc-text-field mdc-text-field--outlined mdc-text-field--no-label">
                  <div class="mat-mdc-form-field-flex">
                    <div class="mdc-notched-outline mdc-notched-outline--no-label">
                      <div class="mdc-notched-outline__leading"></div>
                      <div class="mdc-notched-outline__notch"></div>
                      <div class="mdc-notched-outline__trailing"></div>
                    </div>
                    <div class="mat-mdc-form-field-infix">
                      <input matInput placeholder="Search" aria-label="Search" class="mat-mdc-input-element mat-mdc-form-field-input-control mdc-text-field__input">
                    </div>
                  </div>
                </div>
              </mat-form-field>
            </span>
          </span>`;
  
        // Set the inner HTML of the optgroup
        if(this.searchGroup){
          this.searchGroup.innerHTML = formFieldHtml;
        // Get the input element and wire up events
          const input = this.searchGroup.querySelector('input');

          if (input) {
            this.renderer.listen(input, 'input', (event: Event) => {
              this.searchControl.setValue((event.target as HTMLInputElement).value);
            });
    
            this.renderer.listen(input, 'keydown', (event: Event) => {
              event.stopPropagation();
            });
          }
        }
  
      }
  
      // Insert at the beginning of the panel
      if (panel.firstChild) {
        this.renderer.insertBefore(panel, this.searchGroup, panel.firstChild);
      } else {
        this.renderer.appendChild(panel, this.searchGroup);
      }
  }

  private filterOptions(searchTerm: string) {
    if (!this.originalOptions) return;

    this.matSelect.options.forEach(option => {
      const optionText = this.isObjectsList && this.displayKey && option.value?.[this.displayKey]
        ? option.value[this.displayKey].toLowerCase()
        : option.viewValue.toLowerCase();
      
      const element = option._getHostElement();
      if (optionText.includes(searchTerm)) {
        this.renderer.removeStyle(element, 'display');
      } else {
        this.renderer.setStyle(element, 'display', 'none');
      }
    });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}